import { DataTableBaseOptions, DataTableProps } from './types';
import {
    PaginationState,
    RowData,
    SortingState,
    Updater,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { memo, useCallback, useMemo, useRef, useState } from 'react';

import DataTableHeadRow from './components/data-table-header-row';
import DataTablePager from './components/data-table-pager';
import DataTableRow from './components/data-table-body-row';
import React from 'react';
import clsx from 'clsx';
import styles from './data-table.module.scss';
import { useResizeObserver } from '@react-hookz/web';

const initialPaginationState = {
    pageIndex: 0,
    pageSize: 10,
};

export const DATA_TABLE_COLUMN_AUTO_WIDTH = -1;

const DataTable = <TData extends RowData>({
    id = 'data-table',
    small = false,
    scrollable = false,
    columnResizeMode = 'onChange',
    state: extState,
    onSortingChange,
    onPaginationChange,
    rowsCount,
    pageCount,
    paginated = false,
    onPreSortingChange,
    tableRef,
    ...restOptions
}: DataTableProps<TData>) => {
    const options: DataTableBaseOptions<TData> = {
        enableColumnResizing: false,
        defaultColumn: {
            minSize: 100,
            enableSorting: false,
        },
        ...restOptions,
    };

    const [sortingState, setSortingState] = useState<SortingState>(extState?.sorting ?? []);
    const [paginationState, setPaginationState] = useState<PaginationState>(
        extState?.pagination ?? initialPaginationState
    );

    const resolvedPageCount = useMemo(() => {
        if (pageCount) {
            return pageCount;
        }

        if (!!rowsCount && paginationState.pageSize > 0) {
            return Math.ceil(rowsCount / paginationState.pageSize);
        }

        return undefined;
    }, [pageCount, paginationState.pageSize, rowsCount]);

    const handlePaginationChange = useCallback(
        (stateUpdater: Updater<PaginationState>) => {
            if (typeof stateUpdater === 'function') {
                const newPaginationState = stateUpdater(paginationState);

                setPaginationState(newPaginationState);

                onPaginationChange?.(newPaginationState);
            }
        },
        [onPaginationChange, paginationState]
    );

    const handleSortingChange = useCallback(
        (stateUpdater: Updater<SortingState>) => {
            if (typeof stateUpdater === 'function') {
                let newSortingState = stateUpdater(sortingState);

                if (onPreSortingChange) {
                    newSortingState = [...onPreSortingChange(newSortingState)];
                }

                setSortingState(newSortingState);

                onSortingChange?.(newSortingState);
            }
        },
        [onSortingChange, onPreSortingChange, sortingState]
    );

    const {
        getHeaderGroups,
        getRowModel,
        getState,
        getPageCount,
        setPageIndex,
        options: { meta: tableMeta },
    } = useReactTable<TData>({
        ...options,
        pageCount: resolvedPageCount,
        state: {
            sorting: sortingState,
            pagination: paginationState,
        },
        onSortingChange: handleSortingChange,
        onPaginationChange: handlePaginationChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: options.manualSorting ? undefined : getSortedRowModel(),
        getPaginationRowModel: !paginated || options.manualPagination ? undefined : getPaginationRowModel(),
    });
    const state = getState();

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState<number>();
    const [containerWidth, setContainerWidth] = useState<number>();

    const handleResize = useCallback(
        ({ contentRect }: { contentRect: DOMRectReadOnly }) => {
            if (scrollable) {
                setContainerHeight(contentRect.height);
                setContainerWidth(contentRect.width);
            }
        },
        [scrollable]
    );

    useResizeObserver(containerRef, handleResize, scrollable);

    const containerClasses = clsx(styles.container, {
        [styles.containerFixed]: !scrollable,
    });

    const wrapperClasses = clsx(styles.wrapper, {
        [styles.wrapperScrollable]: scrollable,
    });

    const tableClasses = clsx(styles.table, {
        [styles.tableSmall]: small,
        [styles.tableStickyHeader]: scrollable,
    });

    const headerGroups = getHeaderGroups();
    const rows = getRowModel().rows;

    const tableStyle = scrollable ? { height: containerHeight, width: containerWidth } : undefined;

    return (
        <div id={id} className={containerClasses} data-testid={id}>
            <div className={wrapperClasses} ref={containerRef} data-testid={`${id}-wrapper`}>
                <table className={tableClasses} style={tableStyle} ref={tableRef}>
                    <thead className={styles.header}>
                        {headerGroups.map((headerGroup) => (
                            <DataTableHeadRow
                                key={headerGroup.id}
                                headerGroup={headerGroup}
                                columnResizeMode={columnResizeMode}
                                columnDeltaOffset={state.columnSizingInfo.deltaOffset}
                            />
                        ))}
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const isHighlighted = tableMeta?.isRowHighlighted?.(row.original) ?? false;

                            return <DataTableRow key={row.id} row={row} isHighlighted={isHighlighted} />;
                        })}
                    </tbody>
                </table>
            </div>
            {paginated && (
                <DataTablePager
                    id={`${id}-pager`}
                    pageIndex={paginationState.pageIndex}
                    pageCount={getPageCount()}
                    setPageIndex={setPageIndex}
                />
            )}
        </div>
    );
};

export default memo(DataTable) as typeof DataTable;
